// `SnoozeMenu` — DropdownMenu offering 1h / 4h / 24h / custom snooze
// (US-165 gap-design).
//
// Renders inside `RelanceRow` next to the Approve / Skip buttons. Three
// quick-pick options compute the ISO timestamp client-side from `Date.now()`;
// the "Custom date…" entry opens a small Dialog where the user picks an
// `<input type="datetime-local">` value and hits "Snooze". On confirm the
// host's `onSnooze(iso)` callback is invoked.
//
// We intentionally don't manage the API call here — the parent owns the
// `useSnoozeRelance` mutation so it can correlate the row id, surface the
// toast, and propagate the optimistic update.

import { useState } from 'react';

import { useT } from '@barel/i18n';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
} from '@barel/ui';

const HOUR_MS = 60 * 60 * 1000;

export interface SnoozeMenuProps {
  /** Triggered with the chosen ISO 8601 timestamp. */
  onSnooze: (snoozeUntilIso: string) => void;
  /** Disable the trigger (e.g. while a parallel mutation is in flight). */
  disabled?: boolean;
  /** Stable test id for the wrapper. */
  'data-testid'?: string;
}

/** Format a `Date` as the local-time string `<input type="datetime-local">` expects. */
function toInputLocal(date: Date): string {
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function SnoozeMenu({ onSnooze, disabled, ...rest }: SnoozeMenuProps) {
  const t = useT('relances');
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState<string>(() =>
    toInputLocal(new Date(Date.now() + 24 * HOUR_MS)),
  );
  const [customError, setCustomError] = useState<string | null>(null);

  const fireQuick = (deltaMs: number): void => {
    const iso = new Date(Date.now() + deltaMs).toISOString();
    onSnooze(iso);
  };

  const confirmCustom = (): void => {
    if (!customValue) {
      setCustomError(t('queue.actionError'));
      return;
    }
    const parsed = new Date(customValue);
    if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now()) {
      setCustomError(t('queue.actionError'));
      return;
    }
    setCustomError(null);
    setCustomOpen(false);
    onSnooze(parsed.toISOString());
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            data-testid={rest['data-testid'] ?? 'snooze-menu-trigger'}
          >
            {t('queue.snooze')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              fireQuick(HOUR_MS);
            }}
            data-testid="snooze-1h"
          >
            {t('queue.snoozeOption1h')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              fireQuick(4 * HOUR_MS);
            }}
            data-testid="snooze-4h"
          >
            {t('queue.snoozeOption4h')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              fireQuick(24 * HOUR_MS);
            }}
            data-testid="snooze-24h"
          >
            {t('queue.snoozeOption24h')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              setCustomOpen(true);
            }}
            data-testid="snooze-custom"
          >
            {t('queue.snoozeOptionCustom')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent size="sm" data-testid="snooze-custom-dialog">
          <DialogHeader title={t('queue.snoozeCustomTitle')} onClose={() => setCustomOpen(false)} />
          <DialogBody>
            <label className="flex flex-col gap-1.5">
              <span className="text-2xs font-semibold uppercase tracking-wider text-secondary">
                {t('queue.snoozeCustomLabel')}
              </span>
              <Input
                type="datetime-local"
                value={customValue}
                onChange={(event) => {
                  setCustomValue(event.target.value);
                  setCustomError(null);
                }}
                data-testid="snooze-custom-input"
              />
              {customError ? (
                <span className="text-2xs text-error" role="alert">
                  {customError}
                </span>
              ) : null}
            </label>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={() => setCustomOpen(false)}
              data-testid="snooze-custom-cancel"
            >
              {t('queue.snoozeCustomCancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={confirmCustom}
              data-testid="snooze-custom-confirm"
            >
              {t('queue.snoozeCustomConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
